import {
  LocalizationProvider,
} from '@mui/x-date-pickers/LocalizationProvider';
import {
  DatePicker,
} from '@mui/x-date-pickers/DatePicker';
import {
  TimePicker,
} from '@mui/x-date-pickers/TimePicker';
import {
  AdapterLuxon,
} from '@mui/x-date-pickers/AdapterLuxon';
import TextField from '@mui/material/TextField';
import {
  DateTime,
} from 'luxon';
import {
  getIn,
  useFormikContext,
} from 'formik';
import React, {
  useEffect,
} from 'react';
import {
  isDateOnlyPicker,
  isTimeOnlyPicker,
} from '../../types';
import FieldError from '../../Utils/FieldError';
import {
  FieldView,
} from '../../Utils/FieldView';
import {
  DateTimeFieldValue,
  FieldPropsCommon,
} from '../types';
import {
  onChange,
} from '../utils';

type Props = FieldPropsCommon & DateTimeFieldValue;

const DateTimeField = <FormModel extends object>(props: Props) => {
  const {
    fieldOptions,
    location,
    title,
    viewMode,
    disabled,
  } = props;

  const {
    values,
    handleChange,
    isSubmitting,
    setFieldTouched,
    errors,
    touched,
  } = useFormikContext<FormModel>();
  const value: Date = getIn(values, location);
  const onValueChange = onChange(handleChange, location);
  const error = getIn(errors, location);
  const isTouched = getIn(touched, location);

  useEffect(() => {
    if (isSubmitting) {
      setFieldTouched(location);
    }
  }, [isSubmitting, location, setFieldTouched]);

  if (viewMode) {
    return (
      <FieldView
        title={title}
        value={() => (
          <div>
            {
            isTimeOnlyPicker(fieldOptions) && !isDateOnlyPicker(fieldOptions) && (
              DateTime.fromJSDate(value).toLocaleString({
                hour: 'numeric', minute: '2-digit',
              })
            )
          }
            {
            !isTimeOnlyPicker(fieldOptions) && isDateOnlyPicker(fieldOptions) && (
              DateTime.fromJSDate(value).toLocaleString({
                weekday: 'short', month: 'short', day: 'numeric',
              })
            )
          }
            {
            !isTimeOnlyPicker(fieldOptions) && !isDateOnlyPicker(fieldOptions) && (
              DateTime.fromJSDate(value).toLocaleString({
                month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
              })
            )
          }
          </div>
        )}
      />
    );
  }

  return (
    <div
      style={{
        margin: 8,
      }}
    >
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <>
          {
            !isTimeOnlyPicker(fieldOptions) && (
              <DatePicker
                label={title}
                value={value}
                disabled={disabled}
                onClose={() => setFieldTouched(location)}
                renderInput={(datePickerProps) => (
                  <TextField
                    {...datePickerProps}
                    {...(fieldOptions.datePickerProps?.TextFieldComponent
                      ? {
                        component: fieldOptions.datePickerProps.TextFieldComponent,
                      }
                      : {})}
                    {...(fieldOptions.datePickerProps?.InputProps
                      ? {
                        // Cast InputProps to ensure type compatibility
                        InputProps: fieldOptions.datePickerProps.InputProps as any,
                      }
                      : {})}
                  />
                )}
                {...(fieldOptions.datePickerProps
                  // Filter out props that are not compatible with the new API
                  ? Object.fromEntries(
                    Object.entries(fieldOptions.datePickerProps).filter(
                      ([key]) => !['TextFieldComponent', 'InputProps'].includes(key),
                    ),
                  )
                  : {}
                )}
                onChange={(date: { toJSDate: () => Date; }|null) => {
                  if (date) {
                    onValueChange(date.toJSDate());
                    if (fieldOptions.onValueChanged) {
                      fieldOptions.onValueChanged(date.toJSDate());
                    }
                  }
                }}
              />
            )
          }
          {
            !isDateOnlyPicker(fieldOptions) && (
              <TimePicker
                label={`${title} Time Picker`}
                value={value}
                disabled={disabled}
                onClose={() => setFieldTouched(location)}
                renderInput={(timePickerProps) => (
                  <TextField
                    {...timePickerProps}
                    {...(fieldOptions.timePickerProps?.TextFieldComponent
                      ? {
                        component: fieldOptions.timePickerProps.TextFieldComponent,
                      }
                      : {})}
                    {...(fieldOptions.timePickerProps?.InputProps
                      ? {
                        // Cast InputProps to ensure type compatibility
                        InputProps: fieldOptions.timePickerProps.InputProps as any,
                      }
                      : {})}
                  />
                )}
                {...(fieldOptions.timePickerProps
                  // Filter out props that are not compatible with the new API
                  ? Object.fromEntries(
                    Object.entries(fieldOptions.timePickerProps).filter(
                      ([key]) => !['TextFieldComponent', 'InputProps'].includes(key),
                    ),
                  )
                  : {}
                )}
                onChange={(date: { toJSDate: () => any; }|null) => {
                  if (date) {
                    onValueChange(date.toJSDate());
                  }
                }}
              />
            )
          }
          <FieldError
            errors={error}
            touched={isTouched}
          />
        </>
      </LocalizationProvider>
    </div>
  );
};

export default DateTimeField;
